/** QuickPlan — Store zustand : projet ouvert, bibliothèque, sélection, vue, undo/redo,
 *  auto-save, et tour de l'assistant (flux SSE). */
import { create } from 'zustand';
import { api } from '../api/client.js';
import { makeLookup } from '../../shared/library/index.js';
import * as ops from '../../shared/ops.js';
import { projectBounds } from '../geom.js';

const clone = (v) => JSON.parse(JSON.stringify(v));
const HISTORY_CAP = 60;

let saveTimer = null;
let transientSnapshot = null;
let eventSource = null;
let aiSnapshotPushed = false;
let lastBox = 0;

// Lookup mémorisé : reconstruit seulement si la bibliothèque ou les composants du projet changent.
let lookupCache = { lib: null, comps: null, fn: () => null };
function lookupFor(library, project) {
  const comps = project?.components ?? null;
  if (lookupCache.lib !== library || lookupCache.comps !== comps) {
    const custom = (library?.components ?? []).filter((c) => c.origin !== 'builtin');
    lookupCache = { lib: library, comps, fn: makeLookup(project, custom) };
  }
  return lookupCache.fn;
}

export const useProjectStore = create((set, get) => ({
  // ── État ──
  me: null,
  authError: null,          // { status, message } : hors session (401) ou application non attribuée (403)
  projects: [],
  project: null,
  library: { components: [], templates: [] },
  selection: [],            // ids d'instances
  hidden: {},               // masquage de session (en plus de inst.hidden) — non utilisé pour l'instant
  mode: 'design',           // design | bom | notice
  leftTab: 'library',       // library | structure
  rightTab: 'props',        // props | chat
  view: 'persp',            // persp | front | top | right
  gizmo: 'translate',       // translate | rotate
  showPorts: false,
  showEdges: true,
  showFixings: true,
  tool: 'select',           // select | fixation
  fixType: 'qc-vis',        // composant posé par l'outil Fixation
  boxMode: false,           // glisser = sélection par zone (sinon Maj + glisser)
  fitNonce: 0,
  saveState: 'idle',        // idle | saving | saved | error | paused
  statusText: '',
  past: [],
  future: [],
  dialog: null,             // { kind: 'template', template } | null
  ai: { available: null, messages: [], job: null, text: '', status: '', tools: [], tier: 'fast', error: '' },

  lookup() { return lookupFor(get().library, get().project); },

  // ── Identité / projets / bibliothèque ──
  /** Rend true si l'identité de suite est établie. */
  async loadMe() {
    try {
      set({ me: await api('/me'), authError: null });
      return true;
    } catch (e) {
      set({ me: null, authError: e.status === 401 || e.status === 403 ? { status: e.status, message: e.message } : null });
      return false;
    }
  },
  readOnly() { return get().project?.role === 'viewer'; },
  async loadProjects() { set({ projects: await api('/projects') }); },
  async loadLibrary() { set({ library: await api('/library') }); },
  async createProject(name) {
    const project = await api('/projects', { method: 'POST', body: { name } });
    await get().loadProjects();
    get().openLoaded(project);
  },
  async openProject(id) { get().openLoaded(await api(`/projects/${id}`)); },
  openLoaded(project) {
    set({
      project, selection: [], past: [], future: [], mode: 'design', view: 'persp', saveState: 'idle', statusText: '',
      fitNonce: get().fitNonce + 1,
      ai: { ...get().ai, messages: [], job: null, text: '', status: '', tools: [], error: '' },
    });
    get().loadLibrary().catch(() => {});
    get().loadChat().catch(() => {});
  },
  closeProject() {
    clearTimeout(saveTimer);
    eventSource?.close();
    eventSource = null;
    set({ project: null, selection: [], past: [], future: [] });
    get().loadProjects();
  },
  async shareProject(id, { sharedWith, viewers }) {
    await api(`/projects/${id}/share`, { method: 'PUT', body: { sharedWith, viewers } });
    await get().loadProjects();
  },
  async deleteProjectById(id) {
    await api(`/projects/${id}`, { method: 'DELETE' });
    await get().loadProjects();
  },
  async publishComponent(comp) {
    const { origin, ...data } = comp;
    const saved = await api('/library/components', { method: 'POST', body: data });
    await get().loadLibrary();
    return saved;
  },
  async deleteLibraryComponent(id) {
    await api(`/library/components/${id}`, { method: 'DELETE' });
    await get().loadLibrary();
  },

  // ── UI ──
  setMode: (mode) => set({ mode }),
  setLeftTab: (leftTab) => set({ leftTab }),
  setRightTab: (rightTab) => set({ rightTab }),
  setView: (view) => set({ view, fitNonce: get().fitNonce + 1 }),
  setGizmo: (gizmo) => set({ gizmo }),
  toggle: (key) => set({ [key]: !get()[key] }),
  fit: () => set({ fitNonce: get().fitNonce + 1 }),
  setSelection: (selection) => set({ selection: selection ?? [] }),
  select(id, additive = false) {
    if (!additive) return set({ selection: id ? [id] : [] });
    const s = get().selection;
    set({ selection: s.includes(id) ? s.filter((x) => x !== id) : [...s, id] });
  },
  setStatus: (statusText) => set({ statusText }),
  setTool: (tool) => set({ tool, statusText: tool === 'fixation' ? 'Fixation : cliquez une face pour poser — Échap pour terminer' : '' }),
  setFixType: (fixType) => set({ fixType }),
  markBoxed: () => { lastBox = Date.now(); },
  recentlyBoxed: () => Date.now() - lastBox < 300,
  openDialog: (dialog) => set({ dialog }),
  closeDialog: () => set({ dialog: null }),

  // ── Mutations avec historique ──
  /** fn(project, lookup) modifie une copie du projet. Rend ce que rend fn, ou null si refusé. */
  mutate(fn) {
    const { project, past } = get();
    if (!project || get().ai.job) return null;
    if (project.role === 'viewer') { set({ statusText: 'Projet en lecture seule : aucune modification possible.' }); return null; }
    const draft = clone(project);
    let out;
    try {
      out = fn(draft, lookupFor(get().library, draft));
    } catch (e) {
      set({ statusText: `⚠ ${e.message}` });
      return null;
    }
    set({ project: draft, past: [...past.slice(-HISTORY_CAP + 1), project], future: [] });
    get().scheduleSave();
    return out ?? true;
  },
  beginTransient() {
    if (transientSnapshot || !get().project || get().ai.job || get().project.role === 'viewer') return;
    transientSnapshot = get().project;
  },
  applyTransient(fn) {
    const { project } = get();
    if (!project || !transientSnapshot) return;
    const draft = clone(project);
    try { fn(draft, lookupFor(get().library, draft)); } catch { return; }
    set({ project: draft });
  },
  commitTransient() {
    if (!transientSnapshot) return;
    set({ past: [...get().past.slice(-HISTORY_CAP + 1), transientSnapshot], future: [] });
    transientSnapshot = null;
    get().scheduleSave();
  },
  undo() {
    const { project, past, future } = get();
    if (!past.length || !project || get().ai.job) return;
    set({ project: past[past.length - 1], past: past.slice(0, -1), future: [project, ...future] });
    get().pruneSelection();
    get().scheduleSave();
  },
  redo() {
    const { project, past, future } = get();
    if (!future.length || !project || get().ai.job) return;
    set({ project: future[0], past: [...past, project], future: future.slice(1) });
    get().pruneSelection();
    get().scheduleSave();
  },
  pruneSelection() {
    const ids = new Set(get().project?.instances.map((i) => i.id));
    set({ selection: get().selection.filter((id) => ids.has(id)) });
  },

  // ── Actions d'édition courantes ──
  addComponent(componentId) {
    const { project } = get();
    const lookup = get().lookup();
    // Pose à droite de l'existant, au sol, pour ne rien recouvrir.
    let pos = [0, 0, 0];
    const bb = projectBounds(project, lookup);
    if (bb) pos = [Math.ceil((bb.max[0] + 100) / 10) * 10, Math.round(bb.min[1]), 0];
    const comp = lookup(componentId);
    const inst = get().mutate((p, lk) => ops.addInstance(p, lk, { componentId, pos, name: comp?.name }));
    if (inst) set({ selection: [inst.id] });
  },
  insertTemplate(template, params, pos, rot) {
    const out = get().mutate((p, lk) => ops.insertTemplate(p, lk, template, params, { pos, rot }));
    if (out) set({ selection: out.instances.map((i) => i.id), fitNonce: get().fitNonce + 1 });
  },
  deleteSelection() {
    const ids = get().selection;
    if (!ids.length) return;
    get().mutate((p) => ops.deleteInstances(p, ids));
    set({ selection: [] });
  },
  duplicateSelection() {
    const ids = get().selection;
    if (!ids.length) return;
    const out = get().mutate((p, lk) => ops.duplicateInstances(p, ids, [100, 0, 0], lk));
    if (out) set({ selection: out.map((i) => i.id) });
  },
  /** Masque la sélection (ce qu'elle porte disparaît avec elle). */
  hideSelection() {
    const ids = new Set(get().selection);
    if (!ids.size) return;
    get().mutate((p) => { for (const i of p.instances) if (ids.has(i.id)) i.hidden = true; });
    set({ selection: [] });
  },
  showAll() {
    get().mutate((p) => {
      for (const i of p.instances) i.hidden = false;
      for (const g of p.groups) g.hidden = false;
    });
  },
  /** Double-clic : ouvre / ferme l'articulation de la pièce (ou de celle qui la porte). */
  toggleOpen(id) {
    const { project } = get();
    if (!project || !ops.jointOwner(project, get().lookup(), id)) return;
    const out = get().mutate((p, lk) => ops.toggleOpen(p, lk, id));
    if (out?.componentId) set({ statusText: `${out.name || out.componentId} : ${out.open ? 'ouvert' : 'fermé'}` });
  },
  setAllOpen(open) {
    const n = get().mutate((p, lk) => ops.setAllOpen(p, lk, open));
    if (typeof n === 'number') set({ statusText: n ? `${n} articulation(s) ${open ? 'ouverte(s)' : 'fermée(s)'}` : 'Aucune porte ni tiroir dans le projet' });
  },
  /** Outil Fixation : pose `fixType` sur la face cliquée, attachée à la pièce. */
  placeFixation(parentId, pos, normal) {
    const { fixType } = get();
    const inst = get().mutate((p, lk) => ops.addInstance(p, lk, {
      componentId: fixType, parentId, pos, rot: ops.rotZTo(normal), name: lk(fixType)?.name,
    }));
    if (inst) set({ statusText: `Fixation posée sur ${get().project.instances.find((i) => i.id === parentId)?.name ?? 'la pièce'} — Échap pour terminer` });
  },
  /** Attache (ou détache avec null) en conservant la position à l'écran. */
  attach(ids, parentId) {
    get().mutate((p, lk) => { for (const id of ids) ops.setParent(p, lk, id, parentId, true); });
  },
  updateInstance(id, patch) {
    get().mutate((p, lk) => ops.updateInstance(p, lk, id, patch));
  },
  updateProject(fn) { get().mutate((p) => fn(p)); },

  // ── Sauvegarde automatique ──
  scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => get().saveNow(), 800);
  },
  async saveNow() {
    const { project } = get();
    if (!project || project.role === 'viewer') return;
    if (get().ai.job) { set({ saveState: 'paused' }); return; }
    set({ saveState: 'saving' });
    try {
      await api(`/projects/${project.id}`, { method: 'PUT', body: project });
      set({ saveState: 'saved' });
    } catch (e) {
      set({ saveState: 'error', statusText: `Sauvegarde impossible : ${e.message}` });
    }
  },

  // ── Assistant ──
  async loadChat() {
    const { project } = get();
    if (!project) return;
    api('/ai/status').then((st) => set({ ai: { ...get().ai, available: st.available } })).catch(() => {});
    const { messages, running } = await api(`/projects/${project.id}/chat`);
    set({ ai: { ...get().ai, messages } });
    if (running) get().attachJob(running.job);
  },
  setTier: (tier) => set({ ai: { ...get().ai, tier } }),
  async sendPrompt(prompt, images = [], docs = []) {
    const { project, ai, selection } = get();
    if (!project || ai.job || !prompt.trim()) return;
    // On enregistre d'abord : l'assistant doit partir de ce que l'utilisateur voit.
    clearTimeout(saveTimer);
    await get().saveNow();
    set({ ai: { ...get().ai, error: '', pending: prompt, pendingImages: images.map((im) => im.preview), pendingDocs: docs.map(({ name, bytes }) => ({ name, size: bytes })) } });
    try {
      const { job } = await api(`/projects/${project.id}/chat`, {
        method: 'POST',
        body: { prompt, tier: ai.tier, selection, images: images.map(({ name, mediaType, data }) => ({ name, mediaType, data })), files: docs.map(({ name, data }) => ({ name, data })) },
      });
      get().attachJob(job);
    } catch (e) {
      set({ ai: { ...get().ai, error: e.message, pending: null } });
    }
  },
  attachJob(job) {
    eventSource?.close();
    aiSnapshotPushed = false;
    set({ ai: { ...get().ai, job, text: '', status: 'Connexion…', tools: [] } });
    const es = new EventSource(`/api/ai/jobs/${job}/events`);
    eventSource = es;
    es.onmessage = (m) => {
      let ev;
      try { ev = JSON.parse(m.data); } catch { return; }
      const ai = get().ai;
      if (ev.kind === 'resume') set({ ai: { ...ai, text: ev.text, tools: ev.tools, status: ev.status } });
      else if (ev.kind === 'text') set({ ai: { ...ai, text: ai.text + ev.text } });
      else if (ev.kind === 'status') set({ ai: { ...ai, status: ev.text } });
      else if (ev.kind === 'tool') set({ ai: { ...ai, tools: [...ai.tools, ev.name] } });
      else if (ev.kind === 'toolResult' && !ev.ok) set({ ai: { ...ai, status: `Correction : ${ev.error}` } });
      else if (ev.kind === 'project') get().applyServerProject(ev.project);
      else if (ev.kind === 'done' || ev.kind === 'error') get().endJob(ev.kind === 'error' ? ev.message : '');
    };
    es.onerror = () => {
      // Flux coupé (fin normale ou réseau) : on relit l'état du fil.
      if (es.readyState === EventSource.CLOSED || get().ai.job === job) {
        es.close();
        setTimeout(() => {
          if (get().ai.job === job) {
            api(`/projects/${get().project?.id}/chat`).then(({ running }) => {
              if (running?.job === job) get().attachJob(job);
              else get().endJob('');
            }).catch(() => get().endJob(''));
          }
        }, 1500);
      }
    };
  },
  /** Projet modifié par l'assistant : un seul cran d'historique par tour (annulable d'un coup). */
  applyServerProject(project) {
    const cur = get().project;
    if (!cur || cur.id !== project.id) return;
    const past = aiSnapshotPushed ? get().past : [...get().past.slice(-HISTORY_CAP + 1), cur];
    aiSnapshotPushed = true;
    set({ project, past, future: [] });
    get().pruneSelection();
  },
  async endJob(error) {
    eventSource?.close();
    eventSource = null;
    set({ ai: { ...get().ai, job: null, status: '', error: error || '', pending: null } });
    // Le projet du serveur fait foi après un tour.
    const { project } = get();
    if (project) {
      try {
        const fresh = await api(`/projects/${project.id}`);
        if (aiSnapshotPushed) set({ project: fresh });
        set({ saveState: 'saved' });
      } catch { /* garde la copie locale */ }
      await get().loadChat().catch(() => {});
    }
  },
  async cancelJob() {
    const { job } = get().ai;
    if (job) await api(`/ai/jobs/${job}/cancel`, { method: 'POST' }).catch(() => {});
  },
  async clearChat() {
    const { project } = get();
    await api(`/projects/${project.id}/chat`, { method: 'DELETE' });
    set({ ai: { ...get().ai, messages: [] } });
  },
}));
